/**
 * Composable pour charger les données de donation
 * QR code, organisation, et taux de commission
 *
 * ✅ Optimisé pour SSR: utilise useFetch pour charger les données côté serveur
 * Les données sont disponibles instantanément au premier rendu (pas de skeleton)
 */
import { logger } from "~/utils/logger";
import { recuFiscalAnnonce } from "~/utils/recuFiscal";
import {
  useOrganizationPaymentInfo,
  type OrgPaymentProviders,
} from "./useOrganizationPaymentInfo";

export interface DonationInfo {
  name: string;
  description: string;
  organization: string;
  organizationLogo: string;
}

interface DonationData {
  qrCode?: {
    id: string;
    stripe_account_id?: string;
    title?: string;
    description?: string;
    custom_message?: string;
    organization_id?: string | { id: string };
  };
  organization?: {
    id: string;
    name: string;
    logo?: string | null;
    fee_percentage?: number;
    payment_method_configuration_id?: string;
    stripe_account_id?: string;
    is_premium?: boolean;
    payment_providers?: OrgPaymentProviders;
    /** Trust & Safety — l'asso peut être suspendue/bannie (cf. OrgUnavailable). */
    risk_status?: string | null;
    risk_reason?: string | null;
  };
  /**
   * Personnalisation de la page de paiement de l'org. Le flag fiscal est déjà
   * calculé par l'API (RNA + interrupteur asso + habilitation réelle,
   * `computeShowFiscalInfo`) ; l'apparence (couleur, boutons, montants…) est
   * reprise par le widget embarqué.
   */
  paymentPage?: {
    showFiscalInfo?: boolean;
    primaryColor?: string | null;
    buttonStyle?: string | null;
    submitText?: string | null;
    amounts?: number[] | null;
    defaultAmountIndex?: number | null;
    subscriptionsEnabled?: boolean;
    showFeesOption?: boolean;
    impactOptions?: unknown;
  } | null;
  /** Même verdict, toujours présent même quand `paymentPage` est null. */
  showFiscalInfo?: boolean;
}

export const useDonationData = (
  qrCodeId: Ref<string>,
  options?: { apiPath?: string; skipSuffix?: boolean; isPaymentLink?: boolean },
) => {
  const config = useRuntimeConfig();
  const { t } = useI18n();

  // Détecte si c'est un payment-link pour adapter les messages d'erreur
  const isPaymentLink = options?.isPaymentLink ?? false;

  // Utiliser la route API publique pour les données de donation
  // Toutes les pages de paiement (/pay/*, /donate/*) utilisent la même API
  const apiBasePath = computed(() => {
    if (options?.apiPath) return options.apiPath;
    // Route unifiée pour toutes les pages de don
    return "/api/public/qr-codes";
  });

  // Suffixe pour la route API (désactivé pour payment-link qui n'a pas de suffixe)
  const apiSuffix = options?.skipSuffix ? "" : "/donation";

  // États d'erreur
  const isQrCodeValid = ref(true);
  const qrCodeError = ref("");
  const errorTitle = ref(t("don.donationData.erreurTitreDefaut"));
  const hasStripeAccount = ref(true);
  // true = erreur TECHNIQUE (API down / 5xx / réseau / timeout), PAS un lien
  // invalide. Permet à la page d'afficher « réessayer » avec un bouton reload,
  // au lieu de « lien introuvable » (qui accuse à tort le lien). Karim 2026-06-23.
  const isTechnicalError = ref(false);

  // ✅ SSR: Utiliser useFetch pour charger les données côté serveur
  // Les données seront disponibles immédiatement au premier rendu
  const {
    data: ssrData,
    status,
    error: fetchError,
    refresh: refreshData,
  } = useFetch<{ success: boolean; data?: DonationData; message?: string }>(
    () => `${config.public.apiUrl}${apiBasePath.value}/${qrCodeId.value}${apiSuffix}`,
    {
      key: `donation-${qrCodeId.value}`,
      // Ne pas exécuter si pas d'ID
      immediate: !!qrCodeId.value,
      // Retry transitoire (timeout/5xx) — éviter le flaky "QR code non
      // disponible" au premier refresh quand l'API met >2s à répondre.
      // Karim 2026-06-21.
      retry: 2,
      retryDelay: 300,
      timeout: 8000,
    }
  );

  // ✅ Computed: isLoading basé sur le status de useFetch
  const isLoading = computed(() => status.value === "pending");

  // ✅ Computed: Données extraites de la réponse SSR
  const donationInfo = computed<DonationInfo>(() => {
    const data = ssrData.value?.data;
    if (!data) {
      return {
        name: t("don.donationData.recolteGeneraleTitre"),
        description: t("don.donationData.recolteGeneraleDescription"),
        organization: t("don.donationData.organisationDonoorParDefaut"),
        organizationLogo: "/newLogo/logo-mark-white.svg",
      };
    }

    return {
      name: data.qrCode?.title || t("don.donationData.recolteGeneraleTitre"),
      description:
        data.qrCode?.custom_message ||
        data.qrCode?.description ||
        data.qrCode?.title ||
        t("don.donationData.recolteGeneraleDescription"),
      organization: data.organization?.name || t("don.donationData.organisationDonoorParDefaut"),
      organizationLogo: data.organization?.logo || "/newLogo/logo-mark-white.svg",
    };
  });

  /**
   * Bandeau « 66% déductible / votre reçu fiscal » autorisé ?
   *
   * Repli sur `false` tant que la réponse n'est pas là ET quand l'API n'envoie
   * pas le champ : on ne promet JAMAIS un reçu fiscal par défaut. Le widget
   * embed le promettait en dur à toutes les associations, y compris à celles
   * qui n'ont pas activé le service (remontée Mission Entraide Bonne Nouvelle,
   * 2026-08-28).
   */
  // Apparence choisie par l'asso pour son lien de paiement (reprise par le
  // widget embarqué : même couleur, mêmes boutons, mêmes montants).
  const paymentPageAppearance = computed(() => ssrData.value?.data?.paymentPage ?? null);

  const showFiscalInfo = computed<boolean>(
    () => recuFiscalAnnonce(ssrData.value?.data),
  );

  /**
   * Message personnalisé saisi sur le QR CODE (dashboard, iOS, Android).
   *
   * Il était chargé depuis le SSR et fondu dans `donationInfo.description`,
   * mais AUCUN template n'affiche cette description : le message n'apparaissait
   * donc nulle part sur la page de don (Karim 2026-08-26). Exposé à part pour
   * que la page puisse le rendre, et sans les replis de `description` — on veut
   * savoir si l'association a VRAIMENT écrit un message, pas récupérer le titre
   * du QR code ni le texte générique.
   */
  const qrCustomMessage = computed<string | null>(() => {
    const m = ssrData.value?.data?.qrCode?.custom_message;
    return typeof m === "string" && m.trim() ? m.trim() : null;
  });

  // organization_id peut arriver soit comme string (relation foreign key),
  // soit comme objet `{ id, ... }` si la requête Directus utilise
  // `organization_id.*` pour expand la relation. On normalise en string.
  const organizationId = computed<string | null>(() => {
    const qrOrgId = ssrData.value?.data?.qrCode?.organization_id;
    if (!qrOrgId) return null;
    if (typeof qrOrgId === "object") return qrOrgId.id ?? null;
    return String(qrOrgId);
  });

  // UUID réel du QR code en BD, ou null si la page est résolue via
  // payment_pages (id synthétique `org-{n}` — pas un vrai QR code).
  // Permet aux pages /pay/[slug] de tracker l'attribution QR sur la
  // transaction quand le slug long du QR physique a été utilisé.
  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const resolvedQrCodeId = computed<string | null>(() => {
    const id = ssrData.value?.data?.qrCode?.id;
    if (!id || typeof id !== "string") return null;
    return UUID_REGEX.test(id) ? id : null;
  });

  // ─── Dérivations paiement (factorisées via useOrganizationPaymentInfo) ──
  // Note : on enrichit `organization` avec un fallback sur
  // `qrCode.stripe_account_id` pour conserver le comportement legacy où
  // l'organisation n'expose pas son stripe_account_id directement (anciens
  // QR codes).
  const organizationForPayment = computed(() => {
    const data = ssrData.value?.data;
    if (!data?.organization) return null;
    if (data.organization.stripe_account_id) return data.organization;
    if (data.qrCode?.stripe_account_id) {
      return {
        ...data.organization,
        stripe_account_id: data.qrCode.stripe_account_id,
      };
    }
    return data.organization;
  });

  const {
    paymentProviders,
    hasActivePaymentProvider,
    stripeAccountId,
    paymentMethodConfigurationId,
    donoorFeeRate,
    donateUs,
    isPremiumPlan,
    organizationLogoUrl,
  } = useOrganizationPaymentInfo(organizationForPayment);

  // ─── Surveillance ───────────────────────────────────────────────────
  // Permet aux pages /pay et /embed d'afficher OrgUnavailable au lieu du
  // formulaire de don quand l'asso est suspendue. Sans ça le visiteur
  // remplissait le formulaire et prenait une 403 opaque à la validation.
  const organizationRiskStatus = computed<string | null>(
    () => ssrData.value?.data?.organization?.risk_status ?? null,
  );
  const organizationRiskReason = computed<string | null>(
    () => ssrData.value?.data?.organization?.risk_reason ?? null,
  );
  const organizationName = computed<string | null>(
    () => ssrData.value?.data?.organization?.name ?? null,
  );

  // Applique l'état d'erreur de fetch (404 / 503 / autre) sur les refs.
  const applyFetchErrorState = (error: unknown) => {
    const err = error as {
      statusCode?: number;
      status?: number;
      message?: string;
      // L'API renvoie son message lisible sous `data.error.message`
      // (cf. paymentLinkController) ; `data.message` existe sur d'autres
      // endpoints. On accepte les deux formes.
      data?: { message?: string; error?: { message?: string } };
    };
    logger.error("❌ Erreur lors du chargement des données:", err);

    // Message destiné au VISITEUR. On ne prend QUE `data.message`, rédigé par
    // notre API pour être lu, jamais `err.message` : pour une erreur $fetch,
    // celui-ci vaut la chaîne technique brute
    //   [GET] "https://api.donoor.org/api/public/payment-link/xxx": 404
    // qui s'affichait telle quelle sur la page (Karim 2026-08-08) — illisible,
    // et elle expose l'URL interne de l'API.
    // Filet supplémentaire : on rejette tout ce qui ressemble encore à du
    // technique (verbe HTTP entre crochets, URL, code brut).
    const looksTechnical = (m?: string) =>
      !m || /^\[|https?:\/\/|\bfetch\b|\bECONN|\bTypeError\b/i.test(m);
    const rawApiMessage = err?.data?.error?.message || err?.data?.message;
    const apiMessage = looksTechnical(rawApiMessage) ? undefined : rawApiMessage;
    const code = err?.statusCode ?? err?.status;
    isTechnicalError.value = false;

    if (code === 404) {
      // 404 = le lien/QR n'existe vraiment pas (supprimé / mauvaise URL).
      isQrCodeValid.value = false;
      errorTitle.value = isPaymentLink
        ? t("don.donationData.pageParaiementIntrouvableTitre")
        : t("don.donationData.qrCodeInvalideTitre");
      qrCodeError.value = apiMessage || (isPaymentLink
        ? t("don.donationData.pageParaiementSupprimeeMessage")
        : t("don.donationData.qrCodeSupprimeMessage"));
    } else if (code === 503) {
      isQrCodeValid.value = false;
      errorTitle.value = t("don.donationData.paiementsIndisponiblesTitre");
      qrCodeError.value = apiMessage || t("don.donationData.paiementsIndisponiblesMessage");
    } else {
      // Tout le reste (5xx, panne réseau, timeout, code absent) = problème
      // TECHNIQUE côté serveur, PAS un lien invalide → message rassurant +
      // bouton « réessayer » (le lien est bon, c'est temporaire). Karim 2026-06-23.
      isQrCodeValid.value = false;
      isTechnicalError.value = true;
      errorTitle.value = t("don.donationData.erreurSurvenueTitre");
      qrCodeError.value = t("don.donationData.erreurTechniqueMessage");
    }
    hasStripeAccount.value = false;
  };

  // Valide les données reçues (présence d'un provider actif Stripe OU PayPal).
  const applyDataValidityState = (data: { success: boolean; message?: string }) => {
    if (data.success) {
      // Vérifier si au moins UN provider de paiement est actif (Stripe OU PayPal).
      // Avant on bloquait uniquement sur Stripe — maintenant on accepte PayPal
      // seul comme méthode valide (cas asso qui a connecté uniquement PayPal).
      if (!hasActivePaymentProvider.value) {
        isQrCodeValid.value = false;
        hasStripeAccount.value = false; // gardé pour compat ascendante UI
        errorTitle.value = t("don.donationData.paiementsBientotDisponiblesTitre");
        qrCodeError.value = t("don.donationData.paiementsBientotDisponiblesMessage");
        logger.warn(
          "⚠️ Aucun provider de paiement actif (ni Stripe ni PayPal)",
        );
      } else {
        isQrCodeValid.value = true;
        // hasStripeAccount reste true si Stripe ou si on tombe sur le
        // fallback PayPal — on garde le nom pour compat, mais la sémantique
        // devient "au moins un provider actif".
        hasStripeAccount.value = true;
        logger.log("✅ Données de donation chargées avec succès (SSR)");
      }
    } else {
      isQrCodeValid.value = false;
      errorTitle.value = isPaymentLink
        ? t("don.donationData.pageParaiementIntrouvableTitre")
        : t("don.donationData.qrCodeInvalideTitre");
      qrCodeError.value = data.message || (isPaymentLink
        ? t("don.donationData.pageParaiementPasValideMessage")
        : t("don.donationData.qrCodePasValideMessage"));
    }
  };

  // ✅ Watcher pour gérer les erreurs de fetch
  watch(
    [fetchError, ssrData],
    ([error, data]) => {
      if (error) {
        applyFetchErrorState(error);
        return;
      }

      // Vérifier si les données sont valides
      if (data) {
        applyDataValidityState(data);
      }
    },
    { immediate: true }
  );

  /**
   * Fonction de rechargement des données (pour rétrocompatibilité)
   * Avec SSR, cette fonction déclenche un refresh des données
   */
  const loadDonationData = async () => {
    if (!qrCodeId.value) {
      logger.warn("⚠️ Pas de QR code ID fourni");
      return;
    }

    // Si les données sont déjà chargées (SSR), pas besoin de recharger
    if (ssrData.value?.success && stripeAccountId.value) {
      logger.log("📦 Données déjà disponibles via SSR");
      return;
    }

    // Sinon, déclencher un refresh (côté client après navigation)
    await refreshData();
  };

  return {
    // États
    isLoading,
    isQrCodeValid,
    qrCodeError,
    errorTitle,
    /** true = erreur technique (API/serveur), pas un lien invalide → afficher
     *  un bouton « réessayer » qui recharge. */
    isTechnicalError,
    /**
     * @deprecated nom historique — sémantique réelle = "au moins un
     * provider (Stripe OU PayPal) opérationnel". Préférer `canAcceptPayments`.
     */
    hasStripeAccount,
    canAcceptPayments: hasStripeAccount,

    // Surveillance (cascade OrgUnavailable côté DonateErrorStates)
    organizationRiskStatus,
    organizationRiskReason,
    organizationName,

    // Données (maintenant des computed pour réactivité SSR)
    donationInfo,
    showFiscalInfo,
    paymentPageAppearance,
    qrCustomMessage,
    organizationId,
    resolvedQrCodeId,
    stripeAccountId,
    paymentProviders,
    hasActivePaymentProvider,
    paymentMethodConfigurationId,
    donoorFeeRate,
    donateUs,
    isPremiumPlan,
    organizationLogoUrl,

    // Actions
    loadDonationData,
  };
};
